import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Row, Col, Flex, Switch, Typography, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import MultiSelect from "components/common/MultiSelect"
import MultiSelectWithSearch from "components/common/MultiSelectWithSearch"
import FilterChips from "ui/FilterChips"
import { ResourceCard } from "../ResourceCard"
import { CrewMemberTable } from "../CrewMemberTable"
import styles from "../../index.module.css"

const { Text, Title } = Typography

export const CrewMemberSection = ({ crewMemberData, selectedCrewMembers, onCrewMemberChange, selectedRoles, onRoleChange, roleOptions, selectedCrewMembersData, onEdit, onRemove, onAddClick }) => {
  const { t } = useTranslation()

  // Transform crew member data to MultiSelect options format
  const crewMemberOptions = [
    { value: "all", label: "Select All" },
    ...(crewMemberData || []).map((member) => ({
      value: String(member.id),
      label: member.name || `${member.first_name || ""} ${member.last_name || ""}`.trim()
    }))
  ]

  // Filter crew member data based on selected roles
  const filteredCrewMemberData = useMemo(() => {
    // If no roles selected, show all selected crew members
    if (!selectedRoles || selectedRoles.length === 0 || selectedRoles.includes("all")) {
      return selectedCrewMembersData
    }

    // Filter by role ID
    return selectedCrewMembersData.filter((member) => {
      const roleId = String(member.role?.id)
      return selectedRoles.includes(roleId)
    })
  }, [selectedCrewMembersData, selectedRoles])

  return (
    <ResourceCard title={t("listings.tabs.resources.select_staff_member")} buttonText={t("listings.tabs.resources.add_new_staff_member")} onAddClick={onAddClick}>
      <Flex align="center" className="mt-m" gap={8}>
        <Switch disabled />
        <Text>{t("listings.tabs.resources.auto_assign_staff_to_experience")}</Text>
        <Tooltip
          title={
            <div>
              <p>{t("listings.tabs.resources.auto_assign_staff_to_bookings_tooltip_on_description")}</p>
              <p>{t("listings.tabs.resources.auto_assign_staff_to_bookings_tooltip_off_description")}</p>
            </div>
          }
        >
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      </Flex>

      <Row gutter={[16, 16]} className="mt-l">
        <Col lg={14}>
          <LabeledField labelKey="Staff Member" className="mb-0">
            <MultiSelectWithSearch
              value={selectedCrewMembers || []}
              onChange={onCrewMemberChange}
              options={crewMemberOptions}
              placeholder="Select Staff Member"
              searchPlaceholder={t("staff.search_staff") || "Search Staff"}
              style={{ minWidth: "100%", maxWidth: "100%" }}
              t={t}
            />
            {(selectedCrewMembers || []).length === 0 && (
              <Text size="caption" className={styles.emptyExperiencesText}>
                {t("crew_member.no_staff_assigned", { defaultValue: "No staff member assigned yet." })}
              </Text>
            )}
          </LabeledField>
        </Col>
        <Col lg={10} className="mt-xl">
          <MultiSelect
            value={selectedRoles}
            onChange={onRoleChange}
            options={roleOptions}
            placeholder={selectedRoles.length > 0 ? t("Roles") : "All Roles"}
            dropdownClassName={styles.dropdownButton}
            t={t}
          />
        </Col>
      </Row>

      <Flex flex="flex-1">
        <FilterChips selectedValues={selectedRoles} gap={6} options={roleOptions} onChange={onRoleChange} className="flex-wrap" />
      </Flex>

      {(selectedCrewMembers || []).length > 0 && (
        <div>
          <Title level={5} className={`mt-5 ${styles.selectedExperiencesTitle}`}>
            Assigned Staff Members {selectedRoles?.length > 0 && !selectedRoles.includes("all") && `(Filtered: ${filteredCrewMemberData.length}/${selectedCrewMembersData.length})`}
          </Title>
          <CrewMemberTable data={filteredCrewMemberData} onEdit={onEdit} onRemove={onRemove} />
        </div>
      )}
    </ResourceCard>
  )
}

CrewMemberSection.propTypes = {
  crewMemberData: PropTypes.array.isRequired,
  selectedCrewMembers: PropTypes.array.isRequired,
  onCrewMemberChange: PropTypes.func.isRequired,
  selectedRoles: PropTypes.array.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  roleOptions: PropTypes.array.isRequired,
  selectedCrewMembersData: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired
}
